// Foundations
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { skip, Subscription } from 'rxjs';

// Services
import { AnalyticsService } from '@app/services/analytics.service';
import { InterviewRequestService } from '@app/services/interview-request.service';
import { MyProfileService } from '@app/services/my-profile.service';
import { GetLocationGQL } from '@app/services/graphql.service';

// Models
import { PetTileFormInfo } from '@app/components/interview-request/pet-tile-form/pet-tile-form.component';

@Component({
  selector: 'app-step-two',
  templateUrl: './step-two.component.html',
  styleUrls: ['./step-two.component.scss'],
})
export class StepTwoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tabIndexPrompt: number;
  @Output() submitted = new EventEmitter();

  // 0 -> Step Two
  // 1 -> Change Location
  // 2 -> Search Location
  step = 0;

  form: FormGroup;
  campId: number;
  selectedPets: PetTileFormInfo[] = [];

  //
  defaultLocationSubscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    public myProfileService: MyProfileService,
    private _analytics: AnalyticsService,
    public interviewRequestService: InterviewRequestService,
    public getLocationQuery: GetLocationGQL
  ) {
    this.campId = +this.route.snapshot.queryParams.campId;

    // Create the form
    this.form = this.fb.group({
      campId: [null],
    });
    if (this.campId) {
      this.form.patchValue({
        campId: this.campId,
      });
    } else if (this.interviewRequestService.selectedLocation) {
      this.form.patchValue({
        campId: this.interviewRequestService.selectedLocation.id,
      });
    }
  }

  // Life cycles
  // ------------------------------------------------------------------------------------

  ngOnInit(): void {
    if (this.campId) {
      // Fetch the location data
      this.getLocationQuery
        .fetch({ locationId: this.campId.toString() })
        .subscribe((result) => {
          // If error, use "preferred location"
          if (result.errors) {
            this.setDefaultLocation();
          } else {
            this.interviewRequestService.selectedLocation =
              result.data.getLocation;
          }
        });
    } else if (!this.interviewRequestService.selectedLocation) {
      this.setDefaultLocation();
    }
  }

  ngOnChanges() {
    if (this.tabIndexPrompt === 1) {
      this.selectedPets = this.interviewRequestService.readInterviewPetList();
    }
  }

  ngOnDestroy() {
    this.defaultLocationSubscription.unsubscribe();
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  handlePageContent(step): void {
    //
    if (step === 0) {
      this._analytics.logButtonTap('interviewRequest_editCampLocation');
    }

    //
    this.step = step;

    if (step === 0 && this.interviewRequestService.selectedLocation) {
      this.form.patchValue({
        campId: this.interviewRequestService.selectedLocation.id,
      });
    }
  }

  submitRequest() {
    this._analytics.logButtonTap('interviewRequest_submit');
    this.submitted.emit();
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private setDefaultLocation() {
    this.defaultLocationSubscription = this.myProfileService.preferredLocation$
      .pipe(skip(1))
      .subscribe((preferredLocation) => {
        this.interviewRequestService.selectedLocation = preferredLocation;
        this.form.get('campId').patchValue(preferredLocation.id);
      });
  }
}
